import Loading from "./Loading";

/**
 * Blocking full-screen overlay for irreversible operations (placing an order,
 * taking a payment) where the user must not navigate away.
 *
 * Renders nothing when `open` is false, so call sites don't need their own
 * conditional.
 *
 * @param {boolean} open
 * @param {string} [text]         Spinner label, e.g. "Processing your order..."
 * @param {string} [detail]       What is happening and roughly how long.
 * @param {string} [redirectNote] What happens when it finishes.
 *
 * Stacks at `--z-toast` (800): above the sticky header (100), dropdowns (300),
 * the cart drawer (500) and the DS `Dialog` (600). react-hot-toast's container
 * (ToastProvider) uses the same --z-toast and wins only because <Toaster>
 * renders after {children}; keep that order or toasts hide behind this. It used to be Tailwind's `z-50`, which
 * is under every design-system tier — the header, account menu, search and
 * cart drawer stayed clickable while a card was being charged, and an overlay
 * started inside a Dialog rendered behind it (Oct 2026 review). One value for
 * every instance; callers don't choose a tier.
 */
export default function ProcessingOverlay({
  open,
  text = "Processing...",
  detail,
  redirectNote,
}) {
  if (!open) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-busy="true"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[var(--z-toast)]"
    >
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
        <Loading text={text} />
        <div className="mt-4 space-y-2">
          <p className="text-gray-700 font-medium">
            Please don&apos;t close this window
          </p>
          {detail && <p className="text-sm text-gray-600">{detail}</p>}
          {redirectNote && (
            <p className="text-xs text-gray-500 mt-3">{redirectNote}</p>
          )}
        </div>
      </div>
    </div>
  );
}
